import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { ChapterCharacterLayer } from '../types/characterLayerTypes'
import type { PiMatrixAgentIndex, PiMatrixAgentRow, PiMatrixAgentSnapshot } from '../types/piMatrixAgent'
import type { ChapterStructuralRangeLayer, StructuralRange } from '../types/structuralRangeTypes'
import { sha256Hex } from './sha256'
import { buildStructuralRowOrder, STRUCTURAL_RANGE_KIND_LABELS } from './structuralRanges'
import { structuralNarrativeIncrement } from './structuralRangeTaxonomy'

const CONTRACT_VERSION = '0.3.0'

export interface PiMatrixAgentSnapshotBuildSample {
  durationMs: number
  rowCount: number
  witnessCount: number
  serializedCharacters: number
  buildKind: 'direct' | 'initial' | 'scheduled' | 'explicit'
}

type PiMatrixAgentSnapshotBuildObserver = (sample: PiMatrixAgentSnapshotBuildSample) => void

let piMatrixAgentSnapshotBuildObserver: PiMatrixAgentSnapshotBuildObserver | null = null

export const setPiMatrixAgentSnapshotBuildObserver = (
  observer: PiMatrixAgentSnapshotBuildObserver | null,
): void => {
  piMatrixAgentSnapshotBuildObserver = observer
}

export const isPiMatrixAgentProposalCurrent = ({
  proposalBaseSnapshotId,
  currentSnapshotId,
  syncedSnapshotId,
}: {
  proposalBaseSnapshotId: string
  currentSnapshotId: string
  syncedSnapshotId: string
}): boolean => Boolean(
  proposalBaseSnapshotId
  && proposalBaseSnapshotId === currentSnapshotId
  && proposalBaseSnapshotId === syncedSnapshotId
)

export const assertPiMatrixAgentPatchCurrent = (
  patchBaseSnapshotId: string,
  currentSnapshotId: string,
): void => {
  if (!patchBaseSnapshotId || patchBaseSnapshotId !== currentSnapshotId) {
    throw new Error('This Pi proposal no longer matches the current collation; inspect a fresh proposal')
  }
}

const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalize)
  if (!value || typeof value !== 'object') return value
  return Object.keys(value as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((result, key) => {
      const item = (value as Record<string, unknown>)[key]
      if (item !== undefined) result[key] = canonicalize(item)
      return result
    }, {})
}

const inferRowKinds = (row: AnchoredRow): Pick<PiMatrixAgentRow, 'zone' | 'rowKind' | 'rowIdentityKind'> => {
  const activeMappings = row.unitMappings.filter(mapping => !mapping.excluded)
  if (activeMappings.length <= 1) {
    return {
      zone: 'post_authoritative',
      rowKind: 'witness_extra',
      rowIdentityKind: 'witness_extra'
    }
  }
  return {
    zone: 'post_authoritative',
    rowKind: 'canonical',
    rowIdentityKind: 'london_canonical'
  }
}

const unique = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)))

export const buildPiMatrixAgentCharacterIndex = (
  layer: ChapterCharacterLayer,
): PiMatrixAgentIndex => ({
  id: 'characters',
  label: 'Character Index',
  entries: layer.characters
    .filter(character => character.status !== 'rejected')
    .map(character => {
      const occurrences = layer.occurrences.filter(occurrence => (
        occurrence.status !== 'rejected'
        && [occurrence.character_id, ...(occurrence.referent_character_ids || [])].includes(character.character_id)
      ))
      return {
        id: character.character_id,
        label: character.display_name,
        ...(character.kind ? { kind: character.kind } : {}),
        aliases: character.aliases || [],
        rowIds: unique(occurrences.map(occurrence => occurrence.locator.rowId)),
        witnessIds: unique(occurrences.map(occurrence => occurrence.locator.witnessId)),
        note: character.notes || null,
      }
    }),
})

export const buildPiMatrixAgentStructuralRangeIndex = (
  layer: ChapterStructuralRangeLayer,
  rows: AnchoredRow[],
): PiMatrixAgentIndex => {
  const orderedRows = [...rows].sort((left, right) => (
    left.position - right.position || left.labelNumber - right.labelNumber || left.id.localeCompare(right.id)
  ))
  const rowOrderById = buildStructuralRowOrder(orderedRows)
  const rangeById = new Map(layer.ranges.map(range => [range.id, range]))
  const depthById = new Map<string, number>()
  const levelById = new Map<string, number>()
  const metrics = (range: StructuralRange, visiting = new Set<string>()): [number, number] => {
    const cachedDepth = depthById.get(range.id)
    if (cachedDepth !== undefined) return [cachedDepth, levelById.get(range.id) || 0]
    if (visiting.has(range.id)) return [0, 0]
    const parent = range.parentRangeId ? rangeById.get(range.parentRangeId) : null
    const [parentDepth, parentLevel] = parent
      ? metrics(parent, new Set(visiting).add(range.id))
      : [-1, 0]
    const depth = parentDepth + 1
    const narrativeLevel = parent
      ? parentLevel + structuralNarrativeIncrement(range.kind)
      : 0
    depthById.set(range.id, depth)
    levelById.set(range.id, narrativeLevel)
    return [depth, narrativeLevel]
  }

  return {
    id: 'chapter-structure',
    label: 'Chapter Structure',
    entries: layer.ranges
      .filter(range => range.status === 'confirmed' && range.kind)
      .sort((left, right) => (
        (rowOrderById.get(left.startRowId) ?? Number.MAX_SAFE_INTEGER)
        - (rowOrderById.get(right.startRowId) ?? Number.MAX_SAFE_INTEGER)
      ))
      .map(range => {
        const start = rowOrderById.get(range.startRowId)
        const end = rowOrderById.get(range.endRowId)
        const parent = range.parentRangeId ? rangeById.get(range.parentRangeId) : null
        const [depth, narrativeLevel] = metrics(range)
        return {
          id: range.id,
          label: range.title,
          kind: range.kind ? STRUCTURAL_RANGE_KIND_LABELS[range.kind] : undefined,
          rowIds: start === undefined || end === undefined
            ? []
            : orderedRows.slice(start, end + 1).map(row => row.id),
          note: [
            parent ? `Parent: ${parent.title}.` : 'Root range.',
            `Hierarchy depth: ${depth}.`,
            `Narrative level: ${narrativeLevel}.`,
          ].join(' '),
        }
      }),
  }
}

export const buildPiMatrixAgentSnapshot = ({
  chapterId,
  chapterLabel,
  rows,
  witnesses,
  selectedWitnessId,
  selectedRowId,
  diffEnabled = false,
  diffPivotWitnessId,
  indices = [],
  authority,
}: {
  chapterId: string
  chapterLabel: string
  rows: AnchoredRow[]
  witnesses: Witness[]
  selectedWitnessId?: string
  selectedRowId?: string
  diffEnabled?: boolean
  diffPivotWitnessId?: string
  indices?: PiMatrixAgentIndex[]
  authority?: {
    authorityRevisionId: string
    contentHash: string
  }
}, buildKind: PiMatrixAgentSnapshotBuildSample['buildKind'] = 'direct'): PiMatrixAgentSnapshot => {
  const buildStartedAt = piMatrixAgentSnapshotBuildObserver
    ? (typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now())
    : null
  const snapshotRows: PiMatrixAgentRow[] = [...rows]
    .sort((left, right) => left.position - right.position)
    .map(row => {
      const inferred = inferRowKinds(row)
      return {
        id: row.id,
        label: `${row.labelPrefix}-${row.labelNumber.toString().padStart(3, '0')}`,
        position: row.position,
        zone: inferred.zone,
        rowKind: inferred.rowKind,
        rowIdentityKind: inferred.rowIdentityKind,
        unitMappings: row.unitMappings.map(mapping => ({
          witnessId: mapping.witnessId,
          unitUuid: mapping.unitUuid,
          unitLabel: mapping.unitLabel || null,
          textSnapshot: mapping.textSnapshot || '',
          excluded: Boolean(mapping.excluded)
        }))
      }
    })

  const payload = {
    contractVersion: CONTRACT_VERSION,
    chapter: {
      id: chapterId,
      label: chapterLabel
    },
    witnesses: witnesses.map(witness => ({
      id: witness.id,
      sigla: witness.sigla,
      language: 'ar'
    })),
    rows: snapshotRows,
    selection: selectedWitnessId && selectedRowId
      ? { witnessId: selectedWitnessId, rowId: selectedRowId }
      : undefined,
    diff: diffEnabled && diffPivotWitnessId
      ? { pivotWitnessId: diffPivotWitnessId, enabled: true }
      : undefined,
    indices: indices.length > 0 ? indices : undefined,
  }

  const serializedAuthorityContent = JSON.stringify(canonicalize({
    contractVersion: payload.contractVersion,
    chapter: payload.chapter,
    witnesses: payload.witnesses,
    rows: payload.rows,
    indices: payload.indices,
  }))
  const contentHash = `snapshot-${sha256Hex(serializedAuthorityContent)}`
  const authorityRevisionId = authority?.contentHash === contentHash
    ? authority.authorityRevisionId
    : contentHash

  const snapshot = {
    ...payload,
    snapshotId: authorityRevisionId,
    authorityRevisionId,
    contentHash,
  }
  if (buildStartedAt !== null) {
    const buildFinishedAt = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now()
    piMatrixAgentSnapshotBuildObserver?.({
      durationMs: buildFinishedAt - buildStartedAt,
      rowCount: rows.length,
      witnessCount: witnesses.length,
      serializedCharacters: serializedAuthorityContent.length,
      buildKind,
    })
  }
  return snapshot
}
